import {
  type AllKeys,
  notEmpty,
  type Optional,
  type OptionalRecord,
  type TypeForKey,
} from '@festina-life/core'
import cx from 'classnames'
import get from 'lodash-es/get'
import isEqual from 'lodash-es/isEqual'
import type { ComponentProps, JSX, ReactNode } from 'react'

import { Tooltip } from '../../feedback/tooltip/Tooltip'
import { useUiContext } from '../../theme/uiContext'
import { CompareBadge } from '../compareBadge/CompareBadge'
import { Stack } from '../stack/Stack'
import { ExtraTestInformation } from './ExtraTestInformation'

const isNumber = (value: unknown): value is number => typeof value === 'number'

type OnClick = ComponentProps<typeof CompareBadge>['onClick']

export type CompareValuesProps<
  T extends OptionalRecord,
  K extends AllKeys<T>,
> = {
  current: Optional<T>
  prev: Optional<T>
  currentOnClick?: OnClick
  prevOnClick?: OnClick
  keyPath: K | undefined
  // Compare formatter must handle the fact `compare` or `current` could be unset.
  // This is important when the `keyPath` is undefined.
  // Alternative would be to not call the formatter at all if that's the case,
  // but different formatters may chooose to render the undefined case differently.
  formatter?: (
    value: Optional<TypeForKey<T, K>>,
    originalValue?: Optional<T>,
  ) => Optional<string | number | JSX.Element>
  // If the mapped value for instance is a JSX element. then it cannot be compared.
  // In that case a custom compareValue can be provided.
  formatterForCompare?: (value: Optional<TypeForKey<T, K>>) => string
  align?: 'left' | 'right' | 'center'
  isDiffing: boolean
  extraTestInformationTitle?: string | ReactNode
}

export const CompareValues = <T extends OptionalRecord, K extends AllKeys<T>>({
  current,
  currentOnClick,
  prev,
  prevOnClick,
  keyPath,
  formatter = (x) => (notEmpty(x) ? String(x) : undefined),
  formatterForCompare,
  align = 'left',
  isDiffing,
  extraTestInformationTitle,
}: CompareValuesProps<T, K>) => {
  const settingsContext = useUiContext()
  const isInDebugMode = settingsContext?.isInDebugMode

  type Formatter = typeof formatter
  type FormatterInput = Parameters<Formatter>[0]

  const formatNumberDiff = (a?: FormatterInput, b?: FormatterInput): string =>
    isNumber(a) && isNumber(b) && a !== b
      ? `Diff: ${formatter!((a - b) as FormatterInput)}`
      : ''

  const formatSpecialEmptyValues = (x: ReturnType<Formatter>) =>
    notEmpty(x) && x !== '' ? x : '-'

  const mappedCurrent = (
    keyPath && notEmpty(current) ? get(current, keyPath) : current
  ) as FormatterInput

  const mappedPrev = (
    keyPath && notEmpty(prev) ? get(prev, keyPath) : prev
  ) as FormatterInput

  const formattedCurrent = formatter(mappedCurrent, current)
  const formattedPrev = formatter(mappedPrev, prev)

  const compareValueCurrent =
    formatterForCompare?.(mappedCurrent) ?? formattedCurrent
  const compareValuePrev = formatterForCompare?.(mappedPrev) ?? formattedPrev

  return (
    <div
      className={cx(
        'inline-flex',
        isInDebugMode && keyPath && 'relative group',
        {
          left: 'justify-start',
          right: 'justify-end',
          center: 'justify-center',
        }[align],
      )}
    >
      <Tooltip tip={formatNumberDiff(mappedCurrent, mappedPrev)}>
        {isDiffing && !isEqual(compareValuePrev, compareValueCurrent) ? (
          <Stack className="flex-col items-start" gap="condensed">
            {notEmpty(mappedCurrent) && (
              <CompareBadge onClick={currentOnClick} type="prev">
                {formattedCurrent}
              </CompareBadge>
            )}
            {notEmpty(mappedPrev) && (
              <CompareBadge onClick={prevOnClick} type="new">
                {formattedPrev}
              </CompareBadge>
            )}
          </Stack>
        ) : (
          <CompareBadge onClick={currentOnClick} type="current">
            {formatSpecialEmptyValues(formattedCurrent)}
          </CompareBadge>
        )}
      </Tooltip>
      {isInDebugMode && keyPath && (
        <ExtraTestInformation
          path={keyPath}
          currentVal={current}
          prevVal={prev}
          formatter={formatter}
          title={extraTestInformationTitle}
        />
      )}
    </div>
  )
}
